import {makeAutoObservable, toJS} from "mobx"
import {observer} from "mobx-react"
import React, {useMemo} from "react"

import {Section} from "controls/react/layout/section"
import FormEntry from "controls/react/form/formEntry"
import {linkModel, MobxManager} from "framework/mobx-integration"
import {CellDataSourceType} from "controls/designer/dataSourcesManager/cellDataSourceType"
import {
	MetricValueAsSeverity,
	MetricValueToSeverityHolder
} from "controls/designer/dataSourcesManager/metricValueAsSeverity"
import {TimePeriod, TimePeriodSelector} from "controls/react/form/timePeriodSelector"
import {AntCheckbox} from "controls/react/ant/antCheckbox";
import {CellDataSourceBase} from "controls/designer/dataSourcesManager/cellDataSource";
import {Designer} from "controls/designer/designer";
import {MxCell} from "controls/designer/mxGraphInterfaces";
import {MetricDataSourceElement} from "controls/designer/dataSourcesManager/metricDataSourceElement";
import {DisplayLabelType, TotalMetricType} from "controls/designer/dataSourcesManager/metricShared";
import {DataSourceEditorContext} from "controls/designer/dataSourcesManager/dataSourceEditorContext";
import {DesignerStore} from "controls/designer/designerStore";
import {useStore} from "core/react/useStore";
import {TimePeriodType} from "controls/react/form/timePeriodType";

import {MetricConfiguration} from "areas/metrics/components/MetricSelector/types";
import {IncidentDataSourceElement} from "controls/designer/dataSourcesManager/incidentDataSourceElement";
import {RuleDefinition, RulesConfiguration} from "controls/queryBuilder/ruleDefinition";
import {QueryBuilder} from "controls/queryBuilder/queryBuilder";
import {IncidentsGridStore} from "areas/incidents/IncidentsGridStore";
import {apiFetch} from "framework/api";
import {AntAlert} from "controls/react/ant/antAlert";
import {AntSpin} from "controls/react/ant/antSpin";
import {AccountSelector} from "controls/react/accountSelector";
import {FormEntryNew} from "controls/react/form/formEntryNew";
import {ApplicationState} from "framework/applicationState";

const i18n = require('core/localization/localization').translator({

});


export class IncidentDataSourceEditorStore {
	error = false;
	loading = false;
	mobx = new MobxManager()
	dataSource: CellIncidentDatasource
	filtersConfiguration: RulesConfiguration = {}

	constructor(public designerStore: DesignerStore) {
		makeAutoObservable(this)

		this.dataSource = this.designerStore.dataSourcesManager.selected as CellIncidentDatasource
	}

	async init() {
		this.loading = true
		const incidentsGridStore = new IncidentsGridStore()
		const rulesConfigurationResponse = await apiFetch(incidentsGridStore.getDefaultFilters())
		if (rulesConfigurationResponse.success) {
			this.filtersConfiguration = rulesConfigurationResponse.data
		}else{
			this.error = true
		}
		this.loading = false;
	}

	destroy() {
		this.mobx.destroy()
	}
}

export const IncidentDataSourceEditor = observer(() => {
	const designerStore = React.useContext(DataSourceEditorContext)

	const store = useStore(() => new IncidentDataSourceEditorStore(designerStore),
		{deps: [designerStore.dataSourcesManager.selected]});

	if(store.error){
		return <AntAlert type={"error"} message={i18n('An error occurred while initializing editor')}/>
	}

	if(store.loading){
		return <AntSpin spinning={true}/>
	}

	const dataSource = store.dataSource

	return <Section appearance={'none'} childrenPadding={true}>
		<FormEntryNew model={dataSource} modelField={"accountId"}>
			<AccountSelector />
		</FormEntryNew>
		<FormEntryNew model={dataSource} modelField={"includeSubaccounts"}>
			<AntCheckbox>{i18n('Include subaccounts')}</AntCheckbox>
		</FormEntryNew>
		<QueryBuilder rule={dataSource.filters}
		              config={store.filtersConfiguration}/>
		<MetricValueAsSeverity dataSource={dataSource.valueAsSeverity}/>
	</Section>
})

export class CellIncidentDatasource implements CellDataSourceBase{
	id: string

	accountId: string
	includeSubaccounts: boolean
	type: CellDataSourceType.Incident = CellDataSourceType.Incident
	filters: RuleDefinition = RuleDefinition.emptyGroup()

	valueAsSeverity: MetricValueToSeverityHolder

	constructor() {
		this.accountId = ApplicationState.accountId
		this.valueAsSeverity = {
			metricValueAsSeverity: false,
			hideMetricValue: false,
			metricValueToSeverity: [{
				operator: "gte",
				threshold: null
			},{
				operator: "gte",
				threshold: null
			},{
				operator: "gte",
				threshold: null
			}]
		}
		this.includeSubaccounts = false;

		makeAutoObservable(this)
	}

	attachToCell(designer: Designer, cell: MxCell) {
		return new IncidentDataSourceElement(designer, cell);
	}

	valid(){
		return true;
	}
}
